import { errorFromResponse, networkError, type ApiError, type UnauthorizedError } from "./errors";
import type { TokenStore } from "./token-store";

export interface ClientConfig {
  /** Prefix for every path. "/api/v1" on the same origin; a full URL for native apps. */
  baseUrl?: string;
  tokenStore: TokenStore;
  /** Slug of the club to act in; sent as X-Club. */
  getClub?: () => string | null | undefined;
  /** Called once per rejected token (401 on an authenticated request), after the token is cleared. */
  onUnauthorized?: (error: UnauthorizedError) => void;
  /**
   * Called for every response (also failed ones, with the typed error), before it is returned or thrown.
   * The app uses it to learn the license state from `X-ClubCore-License-Status` and from license errors.
   */
  onResponse?: (info: { status: number; headers: Headers; error?: ApiError }) => void;
  fetch?: typeof fetch;
}

export type QueryValue = string | number | boolean | null | undefined | readonly string[];

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** A file's bytes, sent as they are (an image upload). Use instead of `body`; set `contentType` to match. */
  rawBody?: Blob | ArrayBuffer;
  contentType?: string;
  /** Extra request headers (a file upload's `X-Filename`). */
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
  /** false for sign-in: no token is sent, and a 401 is a normal "wrong password" error. */
  authenticated?: boolean;
  /** Override the X-Club header; null sends none. */
  club?: string | null;
  /**
   * "blob" for a file download (PDF, CSV): resolves to a `Download`, errors are still typed. "stream" resolves to the `Response` itself,
   * unread, for a long-lived body such as server-sent events (errors are still typed). Default "json".
   */
  responseType?: "json" | "blob" | "stream";
}

/** What a file download resolves to. */
export interface Download {
  blob: Blob;
  /** From Content-Disposition, if the server named the file. */
  filename: string | null;
  /** The response headers, for facts a download carries beside the file (a report export says `X-Report-Truncated`). */
  headers: Headers;
}

export interface HttpClient {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>;
  readonly tokenStore: TokenStore;
}

function buildUrl(base: string, path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (Array.isArray(value)) {
      // A list is sent as a repeated parameter (`status=a&status=b`).
      for (const item of value) params.append(key, String(item));
    } else if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const text = params.toString();
  return `${base.replace(/\/$/, "")}${path}${text ? `?${text}` : ""}`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function createHttpClient(config: ClientConfig): HttpClient {
  const baseUrl = config.baseUrl ?? "/api/v1";

  async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const authenticated = options.authenticated ?? true;
    const headers = new Headers({ Accept: "application/json" });
    let sentToken: string | null = null;
    if (authenticated) {
      sentToken = (await config.tokenStore.get()) ?? null;
      if (sentToken) headers.set("Authorization", `Bearer ${sentToken}`);
    }
    for (const [name, value] of Object.entries(options.headers ?? {})) headers.set(name, value);
    const club = options.club === undefined ? config.getClub?.() : options.club;
    if (club) headers.set("X-Club", club);
    let body: string | Blob | ArrayBuffer | undefined;
    if (options.rawBody !== undefined) {
      headers.set("Content-Type", options.contentType ?? "application/octet-stream");
      body = options.rawBody;
    } else if (options.body instanceof Blob) {
      // A file upload (a photo): sent as it is, with the type the browser knows it by.
      if (options.body.type) headers.set("Content-Type", options.body.type);
      body = options.body;
    } else if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(options.body);
    }

    let response: Response;
    try {
      response = await (config.fetch ?? fetch)(buildUrl(baseUrl, path, options.query), {
        method,
        headers,
        body,
        signal: options.signal ?? null,
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause; // let callers see their own cancellation
      throw networkError(cause);
    }

    if (response.ok) {
      config.onResponse?.({ status: response.status, headers: response.headers });
      if (options.responseType === "stream") return response as T;
      if (options.responseType === "blob") {
        const named = /filename="?([^";]+)"?/.exec(response.headers.get("Content-Disposition") ?? "");
        return { blob: await response.blob(), filename: named?.[1] ?? null, headers: response.headers } as T;
      }
      return (response.status === 204 ? undefined : await readBody(response)) as T;
    }
    const error = errorFromResponse(response.status, response.statusText, await readBody(response), response.headers);
    config.onResponse?.({ status: response.status, headers: response.headers, error });
    if (error.kind === "unauthorized" && authenticated) {
      // Only drop the token we actually sent: a newer sign-in must survive a late 401.
      if ((await config.tokenStore.get()) === sentToken) await config.tokenStore.clear();
      config.onUnauthorized?.(error as UnauthorizedError);
    }
    throw error;
  }

  return { request, tokenStore: config.tokenStore };
}
